'use client';

import { useState, useCallback } from 'react';
import type { PostCategory, PostTag } from '@routeroyal/theme-core';

export interface PostFiltersState {
  search: string;
  categorySlug: string | null;
  tagSlugs: string[];
  sortBy: 'publishedAt' | 'viewCount' | 'title';
  sortDirection: 'desc' | 'asc';
}

export interface RouteRoyalPostFiltersProps {
  filters: PostFiltersState;
  onChange: (filters: PostFiltersState) => void;
  categories: PostCategory[];
  tags?: PostTag[];
  showTags?: boolean;
  showSort?: boolean;
  className?: string;
  messages?: {
    searchPlaceholder?: string;
    allCategories?: string;
    allTags?: string;
    sortBy?: string;
    newest?: string;
    oldest?: string;
    popular?: string;
    alphabetical?: string;
    clearFilters?: string;
  };
}

export const defaultPostFilters: PostFiltersState = {
  search: '',
  categorySlug: null,
  tagSlugs: [],
  sortBy: 'publishedAt',
  sortDirection: 'desc',
};

/**
 * Route Royal Post Filters
 * 
 * Professional filtering with blue accents
 */
export function RouteRoyalPostFilters({
  filters,
  onChange,
  categories,
  tags = [],
  showTags = true,
  showSort = true,
  className = '',
  messages: customMessages = {},
}: RouteRoyalPostFiltersProps) {
  const messages = {
    searchPlaceholder: 'Search articles...',
    allCategories: 'All Categories',
    allTags: 'All Tags',
    sortBy: 'Sort by',
    newest: 'Newest First',
    oldest: 'Oldest First',
    popular: 'Most Popular',
    alphabetical: 'A-Z',
    clearFilters: 'Clear Filters',
    ...customMessages,
  };

  const [isTagsOpen, setIsTagsOpen] = useState(false);

  const updateFilter = useCallback(<K extends keyof PostFiltersState>(
    key: K,
    value: PostFiltersState[K]
  ) => {
    onChange({ ...filters, [key]: value });
  }, [filters, onChange]);

  const toggleTag = useCallback((slug: string) => {
    const newTags = filters.tagSlugs.includes(slug)
      ? filters.tagSlugs.filter(t => t !== slug)
      : [...filters.tagSlugs, slug];
    updateFilter('tagSlugs', newTags);
  }, [filters.tagSlugs, updateFilter]);

  const clearFilters = useCallback(() => {
    onChange(defaultPostFilters);
  }, [onChange]);

  const hasActiveFilters = 
    filters.search !== '' ||
    filters.categorySlug !== null ||
    filters.tagSlugs.length > 0;

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex flex-wrap gap-3 items-center">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder={messages.searchPlaceholder}
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition-all"
          />
          {filters.search && (
            <button
              onClick={() => updateFilter('search', '')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Category Filter */}
        <div className="relative">
          <select
            value={filters.categorySlug || ''}
            onChange={(e) => updateFilter('categorySlug', e.target.value || null)}
            className="appearance-none pl-4 pr-10 py-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none cursor-pointer"
          >
            <option value="">{messages.allCategories}</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.slug}>{cat.name}</option>
            ))}
          </select>
          <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* Sort */}
        {showSort && (
          <div className="relative">
            <select
              value={`${filters.sortBy}-${filters.sortDirection}`}
              onChange={(e) => {
                const [sortBy, sortDirection] = e.target.value.split('-') as [PostFiltersState['sortBy'], PostFiltersState['sortDirection']];
                onChange({ ...filters, sortBy, sortDirection });
              }}
              className="appearance-none pl-4 pr-10 py-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none cursor-pointer"
            >
              <option value="publishedAt-desc">{messages.newest}</option>
              <option value="publishedAt-asc">{messages.oldest}</option>
              <option value="viewCount-desc">{messages.popular}</option>
              <option value="title-asc">{messages.alphabetical}</option>
            </select>
            <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        )}

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 px-3 py-2.5 text-sm text-gray-500 hover:text-blue-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            {messages.clearFilters}
          </button>
        )}
      </div>

      {/* Tags */}
      {showTags && tags.length > 0 && (
        <div>
          <button
            onClick={() => setIsTagsOpen(!isTagsOpen)}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-blue-600 mb-2 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <span>Tags</span>
            <svg className={`w-4 h-4 transition-transform ${isTagsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            {filters.tagSlugs.length > 0 && (
              <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                {filters.tagSlugs.length}
              </span>
            )}
          </button>
          
          {isTagsOpen && (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => toggleTag(tag.slug)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    filters.tagSlugs.includes(tag.slug)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RouteRoyalPostFilters;
